import { useState } from "react";
import { DCUIContent, DCUIContentAcuantValidation, DCUIContentValidation } from "@iddataweb/idw-common-ui/types";
import { resources } from "@constants/hardcoded-strings";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { ThemingConfigurationContainer } from "@containers/common/theming-configuration-container/theming-configuration-container";
import { ThemingFormContainer } from "@containers/common/theming-form-container/theming-form-container";
import { ThemingSubHeaderContainer } from "@containers/common/theming-sub-header-container/theming-sub-header-container";
import { DCUIThemingTextContentValidationTypes } from "@enums/dcui-theming-text-content-validation-types";
import { DCUIThemingTextContentAcuantValidationTypes } from "@enums/dcui-theming-text-content-acuant-validation-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { ActiveAcuantPropertyFlags } from "@idw-types/common/active-acuant-property-flags";
import { DCUIThemingTextContentValidationErrors } from "@idw-types/common/validation/common/dcui-theming-validation-errors";
import { ContentPageBody } from "../../../../common/content-page-body/content-page-body";
import { ThemingFormSection } from "../../../../common/theming-form-section/theming-form-section";
import { DCUIThemingTextContentGeneralSection } from "./dcui-theming-text-content-general-section/dcui-theming-text-content-general-section";
import { DCUIThemingTextContentValidationGeneralSection } from "./dcui-theming-text-content-validation-general-section/dcui-theming-text-content-validation-general-section";
import { DCUIThemingTextContentValidationGenericSection } from "./dcui-theming-text-content-validation-generic-section/dcui-theming-text-content-validation-generic-section";
import { DCUIThemingTextContentValidationGenericAcuantSection } from "./dcui-theming-text-content-validation-generic-section/dcui-theming-text-content-validation-generic-acuant-section";
import { DCUIThemingTextContentAcuantGeneralSection } from "./dcui-theming-text-content-general-section/dcui-theming-text-content-acuant-general-section";
import { DCUIThemingTextContentBrowserWarningsSection } from "./dcui-theming-text-content-browser-warnings-section/dcui-theming-text-content-browser-warning-section";
import { DCUIThemingTextContentAcuantCameraSection } from "./dcui-theming-text-content-acuant-camera-section/dcui-theming-text-content-acuant-camera-section";
import { PageLevelTypes } from "@enums/page-level-types";

type DCUIThemingTextContentTabContainerProps = {
    isDisabled: boolean;
    validationErrors: DCUIThemingTextContentValidationErrors;
    dcuiContent: DCUIContent;
    inheritedDCUIContent: DCUIContent;
    pageLevel: PageLevelTypes;
    activeAcuantProperties: ActiveAcuantPropertyFlags;
    updateDCUIContentPropertyValue: (dcuiContent: DCUIContent) => void;
};

export const DCUIThemingTextContentTabContainer = (
    props: DCUIThemingTextContentTabContainerProps
) => {
    const {
        isDisabled,
        validationErrors,
        dcuiContent,
        inheritedDCUIContent,
        pageLevel,
        activeAcuantProperties,
        updateDCUIContentPropertyValue,
    } = props;

    const [labelWidth] = useState(LabelWidthTypes.EXTRA_SMALL);
    const [layout] = useState(LayoutTypes.COLUMN);

    const updateDCUIContentValidationFieldValue = (
        key: keyof DCUIContentValidation,
        value: string
    ) => {
        const newPropertyValue: DCUIContent = {
            ...dcuiContent,
            validation: {
                ...dcuiContent.validation,
                [key]: value,
            },
        };
        updateDCUIContentPropertyValue(newPropertyValue);
    };

    const updateDCUIContentAcuantValidationFieldValue = (
        key: keyof DCUIContentAcuantValidation,
        value: string
    ) => {
        const propertyValue = {
            ...dcuiContent.acuant,
            validation: {
                ...dcuiContent.acuant.validation,
                [key]: value,
            }
        }
        const newPropertyValue: DCUIContent = {
            ...dcuiContent,
            acuant: propertyValue
        };
        updateDCUIContentPropertyValue(newPropertyValue);
    };

    const textContentResources =
        resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.textContent;

    return (
        <div>
            <ContentPageHeader title={textContentResources.title} />
            <ContentPageBody isMaxWidth={true}>
                <ThemingSubHeaderContainer
                    description={textContentResources.description}
                />
                <ThemingConfigurationContainer>
                    <ThemingFormContainer>
                        <ThemingFormSection title={textContentResources.sections.general.title} showSeparator={true}>
                            <DCUIThemingTextContentGeneralSection
                                dcuiContent={dcuiContent}
                                inheritedDCUIContent={inheritedDCUIContent}
                                pageLevel={pageLevel}
                                isDisabled={isDisabled}
                                labelWidth={labelWidth}
                                layout={layout}
                                validationErrors={validationErrors}
                                updateDCUIContentPropertyValue={updateDCUIContentPropertyValue}
                            />
                            <DCUIThemingTextContentAcuantGeneralSection
                                dcuiContent={dcuiContent}
                                inheritedDCUIContent={inheritedDCUIContent}
                                pageLevel={pageLevel}
                                isDisabled={isDisabled}
                                labelWidth={labelWidth}
                                layout={layout}
                                validationErrors={validationErrors}
                                updateDCUIContentPropertyValue={updateDCUIContentPropertyValue}
                            />
                        </ThemingFormSection>
                        <ThemingFormSection title={textContentResources.sections.browserWarnings.title} showSeparator={true}>
                            <DCUIThemingTextContentBrowserWarningsSection
                                dcuiContent={dcuiContent}
                                inheritedDCUIContent={inheritedDCUIContent}
                                pageLevel={pageLevel}
                                isDisabled={isDisabled}
                                labelWidth={labelWidth}
                                layout={layout}
                                validationErrors={validationErrors}
                                updateDCUIContentPropertyValue={updateDCUIContentPropertyValue}
                            />
                        </ThemingFormSection>
                        <ThemingFormSection title={textContentResources.sections.validation.sections.general.title} showSeparator={true}>
                            <DCUIThemingTextContentValidationGeneralSection
                                dcuiContent={dcuiContent}
                                inheritedDCUIContent={inheritedDCUIContent}
                                pageLevel={pageLevel}
                                isDisabled={isDisabled}
                                labelWidth={labelWidth}
                                layout={layout}
                                validationErrors={validationErrors}
                                updateDCUIContentValidationFieldValue={updateDCUIContentValidationFieldValue}
                            />
                        </ThemingFormSection>
                        <>
                            {Object.values(DCUIThemingTextContentValidationTypes).map(value => {
                                return (
                                    <ThemingFormSection
                                        key={`text-content-validation-section-${value}`}
                                        title={textContentResources.sections.validation.sections.generic.title(
                                            textContentResources.sections.validation.sections[value].displayText
                                        )}
                                        showSeparator={true}
                                    >
                                        <DCUIThemingTextContentValidationGenericSection
                                            dcuiContent={dcuiContent}
                                            inheritedDCUIContent={inheritedDCUIContent}
                                            pageLevel={pageLevel}
                                            isDisabled={isDisabled}
                                            labelWidth={labelWidth}
                                            layout={layout}
                                            displayText={textContentResources.sections.validation.sections[value].displayText}
                                            validationType={value}
                                            validationErrors={validationErrors[value]}
                                            updateDCUIContentValidationFieldValue={updateDCUIContentValidationFieldValue}
                                        />
                                    </ThemingFormSection>
                                )
                            })}
                        </>
                        {activeAcuantProperties.hasValidLivenessApikey && (
                            <ThemingFormSection
                                title={textContentResources.sections.validation.sections.generic.title(
                                    textContentResources.sections.validation.sections.acuant.liveness.displayText
                                )}
                                showSeparator={
                                    activeAcuantProperties.hasValidAcuantSDKCaptureProperty ||
                                    activeAcuantProperties.hasValidAcuantSharpnessValidationProperty ||
                                    activeAcuantProperties.hasValidAcuantGlareValidationProperty ||
                                    activeAcuantProperties.hasValidAcuantDPIValidationProperty
                                }
                            >
                                <DCUIThemingTextContentValidationGenericAcuantSection
                                    dcuiContent={dcuiContent}
                                    inheritedDCUIContent={inheritedDCUIContent}
                                    pageLevel={pageLevel}
                                    isDisabled={isDisabled}
                                    labelWidth={labelWidth}
                                    layout={layout}
                                    displayText={textContentResources.sections.validation.sections.acuant.liveness.displayText}
                                    acuantValidationType={DCUIThemingTextContentAcuantValidationTypes.LIVENESS_DETECTION}
                                    validationErrors={validationErrors.livenessDetection}
                                    updateDCUIContentAcuantValidationFieldValue={updateDCUIContentAcuantValidationFieldValue}
                                />
                            </ThemingFormSection>
                        )}
                        {activeAcuantProperties.hasValidAcuantDPIValidationProperty && (
                            <ThemingFormSection
                                title={textContentResources.sections.validation.sections.acuant.generic.title(
                                    textContentResources.sections.validation.sections.acuant.badResolution.displayText
                                )}
                                showSeparator={
                                    activeAcuantProperties.hasValidAcuantSDKCaptureProperty ||
                                    activeAcuantProperties.hasValidAcuantSharpnessValidationProperty ||
                                    activeAcuantProperties.hasValidAcuantGlareValidationProperty
                                }
                            >
                                <DCUIThemingTextContentValidationGenericAcuantSection
                                    dcuiContent={dcuiContent}
                                    inheritedDCUIContent={inheritedDCUIContent}
                                    pageLevel={pageLevel}
                                    isDisabled={isDisabled}
                                    labelWidth={labelWidth}
                                    layout={layout}
                                    displayText={textContentResources.sections.validation.sections.acuant.badResolution.displayText}
                                    acuantValidationType={DCUIThemingTextContentAcuantValidationTypes.BAD_RESOLUTION}
                                    validationErrors={validationErrors.badResolution}
                                    updateDCUIContentAcuantValidationFieldValue={updateDCUIContentAcuantValidationFieldValue}
                                />
                            </ThemingFormSection>
                        )}
                        {activeAcuantProperties.hasValidAcuantGlareValidationProperty && (
                            <ThemingFormSection
                                title={textContentResources.sections.validation.sections.acuant.generic.title(
                                    textContentResources.sections.validation.sections.acuant.glareDetection.displayText
                                )}
                                showSeparator={
                                    activeAcuantProperties.hasValidAcuantSDKCaptureProperty ||
                                    activeAcuantProperties.hasValidAcuantSharpnessValidationProperty
                                }
                            >
                                <DCUIThemingTextContentValidationGenericAcuantSection
                                    dcuiContent={dcuiContent}
                                    inheritedDCUIContent={inheritedDCUIContent}
                                    pageLevel={pageLevel}
                                    isDisabled={isDisabled}
                                    labelWidth={labelWidth}
                                    layout={layout}
                                    displayText={textContentResources.sections.validation.sections.acuant.glareDetection.displayText}
                                    acuantValidationType={DCUIThemingTextContentAcuantValidationTypes.GLARE_DETECTION}
                                    validationErrors={validationErrors.glareDetection}
                                    updateDCUIContentAcuantValidationFieldValue={updateDCUIContentAcuantValidationFieldValue}
                                />
                            </ThemingFormSection>
                        )}
                        {activeAcuantProperties.hasValidAcuantSharpnessValidationProperty && (
                            <ThemingFormSection
                                title={textContentResources.sections.validation.sections.acuant.generic.title(
                                    textContentResources.sections.validation.sections.acuant.blurDetection.displayText
                                )}
                                showSeparator={activeAcuantProperties.hasValidAcuantSDKCaptureProperty}
                            >
                                <DCUIThemingTextContentValidationGenericAcuantSection
                                    dcuiContent={dcuiContent}
                                    inheritedDCUIContent={inheritedDCUIContent}
                                    pageLevel={pageLevel}
                                    isDisabled={isDisabled}
                                    labelWidth={labelWidth}
                                    layout={layout}
                                    displayText={textContentResources.sections.validation.sections.acuant.blurDetection.displayText}
                                    acuantValidationType={DCUIThemingTextContentAcuantValidationTypes.BLUR_DETECTION}
                                    validationErrors={validationErrors.blurDetection}
                                    updateDCUIContentAcuantValidationFieldValue={updateDCUIContentAcuantValidationFieldValue}
                                />
                            </ThemingFormSection>
                        )}
                        {activeAcuantProperties.hasValidAcuantSDKCaptureProperty && (
                            <ThemingFormSection title={textContentResources.sections.acuantCamera.title}>
                                <DCUIThemingTextContentAcuantCameraSection
                                    dcuiContent={dcuiContent}
                                    inheritedDCUIContent={inheritedDCUIContent}
                                    pageLevel={pageLevel}
                                    isDisabled={isDisabled}
                                    labelWidth={labelWidth}
                                    layout={layout}
                                    validationErrors={validationErrors}
                                    updateDCUIContentPropertyValue={updateDCUIContentPropertyValue}
                                />
                            </ThemingFormSection>
                        )}
                    </ThemingFormContainer>
                </ThemingConfigurationContainer>
            </ContentPageBody>
        </div>
    );
};
